import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MarketingNav from '../components/layout/MarketingNav';
import MarketingFooter from '../components/layout/MarketingFooter';

/**
 * JourneyPage — Mental Health Focused Rewrite
 * The 365-day arc framed as developmental growth across mental health domains.
 * Mental wellness includes emotional, mental, and physical wellbeing. Not addiction-specific.
 */

const BRAND = {
  forest: '#2D5A47', forestDark: '#1E3D30', forestLight: '#4A7C67',
  sand: '#D4A574', sandLight: '#FFF8F0',
  blue: '#7BA3B8', blueLight: '#EDF4F8',
  dark: '#1A1A2E', text: '#2C2C2C', muted: '#6B6B7B',
  light: '#F8F6F3', white: '#FFFFFF',
};

const phases = [
  {
    number: 1,
    name: 'Stabilize',
    days: 'Days 1–90',
    dayRange: [1, 90],
    color: BRAND.blue,
    lightColor: BRAND.blueLight,
    icon: '⚓',
    tagline: 'Find your footing before you find your direction.',
    description: 'The first 90 days are not about transformation — they\'re about stabilization. You\'re building the foundation: daily habits, basic coping tools, and the nervous system regulation that makes everything else possible. This phase is designed to feel manageable, even on the hardest days.',
    whatToExpect: 'Some days will feel significant. Others won\'t feel like much at all. That\'s normal. The compound effect of daily practice takes time to become visible. Stay with it.',
    mindFocus: ['Identifying core emotion patterns', 'Basic cognitive restructuring', 'Distress tolerance tools (TIPP, ACCEPTS)', 'Recognizing automatic thoughts', 'Building the daily practice habit'],
    moveFocus: ['Establishing movement as daily ritual', 'Beginner nervous system regulation', 'Breathing for cortisol reduction', 'Low-intensity consistency over performance', 'Sleep-supportive activity timing'],
    journalFocus: ['Daily mood and wellbeing baseline', 'Identifying personal stressors and patterns', 'Basic self-reflection prompts', 'Beginning the pattern record', 'Emotional vocabulary development'],
    milestones: ['Day 7: First full week complete', 'Day 30: First month — nervous system begins adapting', 'Day 60: Patterns becoming visible in your data', 'Day 90: Phase 1 complete — foundation established'],
  },
  {
    number: 2,
    name: 'Understand',
    days: 'Days 91–180',
    dayRange: [91, 180],
    color: BRAND.forest,
    lightColor: '#E8F7F3',
    icon: '🔍',
    tagline: 'Go deeper than coping. Find the pattern beneath the pattern.',
    description: 'With stability established, Phase 2 turns inward. You\'re no longer just managing the symptoms — you\'re examining the underlying structure. Why do certain situations trigger specific responses? What are the core beliefs driving your emotional experience? This is where meaningful insight begins.',
    whatToExpect: 'This phase can feel confrontational. You\'re looking at things you may have been avoiding. That discomfort is the signal that the work is reaching deeper layers. Your coping toolkit from Phase 1 will carry you through.',
    mindFocus: ['Core belief identification and examination', 'Schema work and early experience patterns', 'Advanced cognitive reframing techniques', 'ACT psychological flexibility training', 'Values clarification and identity work'],
    moveFocus: ['Intermediate nervous system regulation', 'Body-based trauma awareness practices', 'Exercise as emotional processing tool', 'Energy management and mood relationship', 'Building movement identity'],
    journalFocus: ['Deeper causal pattern analysis', 'Relationship between past and present responses', 'Belief system examination prompts', 'Behavioral cycle mapping', 'Mid-point progress synthesis'],
    milestones: ['Day 100: First 100 days', 'Day 120: Core beliefs becoming visible', 'Day 150: Patterns in relationships clarifying', 'Day 180: Phase 2 complete — deep understanding established'],
  },
  {
    number: 3,
    name: 'Rebuild',
    days: 'Days 181–270',
    dayRange: [181, 270],
    color: BRAND.sand,
    lightColor: BRAND.sandLight,
    icon: '🏗️',
    tagline: 'Turn insight into structural change.',
    description: 'Phase 3 is where understanding becomes action. You\'ve identified the patterns; now you build the replacement. New habits, restructured relationships, a clearer identity, a life that aligns with your values. This is the most active phase — and often the most rewarding.',
    whatToExpect: 'Change in this phase will feel real and sometimes uncomfortable. Old patterns resist replacement. The work here is persistent, intentional application of everything built in Phases 1 and 2. Expect two steps forward and one step back. That\'s still net progress.',
    mindFocus: ['Behavioral experiment design', 'Interpersonal effectiveness (DEAR MAN, FAST)', 'Advanced ACT committed action', 'Shame resilience and self-compassion', 'Rebuilding self-concept and identity'],
    moveFocus: ['Physical practice as metaphor for persistence', 'Advanced nervous system mastery', 'Integrating movement into lifestyle identity', 'Using physical challenge as confidence builder', 'Community and social movement options'],
    journalFocus: ['Documenting behavioral changes and their impact', 'Relationship repair and boundary narratives', 'Identity reconstruction journaling', 'Gratitude and growth recognition', 'Future self visualization'],
    milestones: ['Day 200: Evidence of structural change emerging', 'Day 225: Relationships beginning to shift', 'Day 250: Identity consolidation visible in writing', 'Day 270: Phase 3 complete — new foundation built'],
  },
  {
    number: 4,
    name: 'Sustain',
    days: 'Days 271–365',
    dayRange: [271, 365],
    color: BRAND.forestDark,
    lightColor: '#E0EDE8',
    icon: '🌳',
    tagline: 'Build a life that doesn\'t require constant management.',
    description: 'Phase 4 isn\'t an ending — it\'s a graduation into sustainable self-direction. You\'re consolidating 270 days of growth into a personal practice that carries forward indefinitely. The centerpiece is your Personal Wellness Playbook: your individualized, living guide to maintaining mental health for the rest of your life.',
    whatToExpect: 'This phase feels different. Lighter. The daily work becomes more about reinforcement than struggle. You\'ll revisit and refine. You\'ll plan for hard times. You\'ll build the maintenance system that means Day 366 doesn\'t feel like falling off a cliff.',
    mindFocus: ['Maintenance planning and anticipatory coping', 'Wellness maintenance framework', 'High-performance mental practice', 'Ongoing growth goal-setting', 'Personal Wellness Playbook — Mind section'],
    moveFocus: ['Sustainable movement lifestyle design', 'Physical practice for long-term neurological health', 'Movement community and accountability', 'Personal movement programming', 'Personal Wellness Playbook — Move section'],
    journalFocus: ['365-day synthesis and reflection', 'Long-term pattern documentation', 'Playbook journaling practice design', 'What worked, what didn\'t, what to keep', 'Personal Wellness Playbook — Journal section'],
    milestones: ['Day 300: Playbook development begins', 'Day 330: Life after Day 365 planning', 'Day 355: Playbook near complete', 'Day 365: Graduation — and a beginning'],
  },
];

const sampleWeek = {
  phase: 1,
  days: [
    { day: 1, theme: 'Arrival', mind: 'What brought you here — without judgment', move: '20-min walk, phone down, noticing what you notice', journal: 'Today I started because...' },
    { day: 2, theme: 'Nervous System Basics', mind: 'Introduction to your stress response — what\'s happening in your body', move: 'Box breathing + cold water face immersion', journal: 'My nervous system today felt like...' },
    { day: 3, theme: 'Emotion Identification', mind: 'The feeling wheel — naming what you\'re actually experiencing', move: '30-min moderate cardio (your choice)', journal: 'The emotion I avoid most is...' },
    { day: 4, theme: 'Thought Patterns', mind: 'Identifying your most frequent automatic thought', move: 'Gentle yoga or stretching — 20 min', journal: 'The thought that plays on repeat is...' },
    { day: 5, theme: 'Coping Inventory', mind: 'What has worked before? What hasn\'t?', move: 'Strength — any resistance training, 20–30 min', journal: 'When things got hard, I used to...' },
    { day: 6, theme: 'Weekly Review', mind: 'What did this week teach you?', move: 'Active rest — walk, swim, or light movement', journal: 'This week I noticed...' },
    { day: 7, theme: 'Milestone', mind: 'One full week. What\'s different?', move: 'Your choice — something you enjoyed', journal: 'One week in. I feel...' },
  ],
};

const JourneyPage = () => {
  const navigate = useNavigate();
  const [activePhase, setActivePhase] = useState(0);
  const [showSampleWeek, setShowSampleWeek] = useState(false);
  const phase = phases[activePhase];

  return (
    <div className="site-canvas" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', color: BRAND.text, minHeight: '100vh' }}>
      <MarketingNav transparent={true} />

      {/* ── HERO ── */}
      <section className="marketing-photo-hero" style={{ backgroundImage: "url('/Walk in Park.png')" }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '1280px', paddingTop: '130px', paddingBottom: '110px' }}>
          <div className="marketing-photo-hero-panel" style={{ maxWidth: '740px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,209,102,0.15)', border: '1px solid rgba(255,209,102,0.3)', borderRadius: '100px', padding: '7px 18px', marginBottom: '24px' }}>
            <span style={{ color: '#ffd166', fontSize: '0.8rem', fontWeight: '600', letterSpacing: '0.07em', textTransform: 'uppercase' }}>The 365-Day Journey</span>
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)', fontWeight: '800', color: '#f1fff8', lineHeight: '1.1', letterSpacing: '-0.025em', marginBottom: '20px' }}>
            A year that changes
            <br /><span style={{ color: '#ffd166' }}>how you handle every year after.</span>
          </h1>
          <p style={{ fontSize: 'clamp(0.95rem, 2vw, 1.15rem)', color: 'rgba(241,255,248,0.80)', lineHeight: '1.7', maxWidth: '540px', margin: '0 0 36px 0' }}>
            Not a quick fix. Not a course you complete. A genuine developmental arc — from stabilizing
            in the middle of difficulty, to building a life that doesn't require constant management.
            365 days. Four phases. One continuous journey, with the optional Faith pathway available throughout.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/register')}
              style={{ background: '#ffd166', color: '#041b12', fontWeight: '700', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: 'none', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f2c14e'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffd166'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Start Day 1
            </button>
            <button onClick={() => setShowSampleWeek(true)}
              style={{ background: 'transparent', color: '#f1fff8', fontWeight: '600', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: '2px solid rgba(241,255,248,0.4)', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(241,255,248,0.8)'; e.currentTarget.style.background = 'rgba(241,255,248,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(241,255,248,0.4)'; e.currentTarget.style.background = 'transparent'; }}>
              Preview Week 1
            </button>
          </div>
          </div>
        </div>
      </section>

      {/* ── PHASE OVERVIEW TIMELINE ── */}
      <section style={{ background: BRAND.white, padding: 'clamp(64px, 10vw, 100px) 24px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)', fontWeight: '800', color: BRAND.dark, letterSpacing: '-0.02em', marginBottom: '12px' }}>
              Four phases. One continuous arc.
            </h2>
            <p style={{ fontSize: '1rem', color: BRAND.muted, lineHeight: '1.65', maxWidth: '500px', margin: '0 auto' }}>
              Each phase builds on the last. You don't skip ahead. You don't fall behind. You move through it. The optional Faith pathway remains available in every phase.
            </p>
          </div>

          {/* Visual timeline */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', marginBottom: '48px' }}>
            {phases.map((p, i) => (
              <div key={p.number}>
                <div style={{ height: '8px', background: p.color, borderRadius: i === 0 ? '8px 0 0 8px' : i === 3 ? '0 8px 8px 0' : '0', marginBottom: '12px' }} />
                <p style={{ fontSize: '0.7rem', fontWeight: '700', color: p.color, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '2px' }}>{p.days}</p>
                <p style={{ fontSize: '0.95rem', fontWeight: '800', color: BRAND.dark, margin: 0 }}>{p.name}</p>
              </div>
            ))}
          </div>

          {/* Phase selector */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '32px' }}>
            {phases.map((p, i) => (
              <button key={p.number} onClick={() => setActivePhase(i)}
                style={{ background: activePhase === i ? p.color : '#F5F4F2', color: activePhase === i ? BRAND.white : BRAND.muted, border: 'none', borderRadius: '12px', padding: '16px 8px', cursor: 'pointer', transition: 'all 0.2s ease', textAlign: 'center' }}
                onMouseEnter={e => { if (activePhase !== i) { e.currentTarget.style.background = '#ECEAE7'; e.currentTarget.style.color = BRAND.dark; } }}
                onMouseLeave={e => { if (activePhase !== i) { e.currentTarget.style.background = '#F5F4F2'; e.currentTarget.style.color = BRAND.muted; } }}>
                <div style={{ fontSize: '1.2rem', marginBottom: '4px' }}>{p.icon}</div>
                <div style={{ fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '2px', opacity: activePhase === i ? 1 : 0.6 }}>{p.days}</div>
                <div style={{ fontSize: '0.95rem', fontWeight: '800' }}>{p.name}</div>
              </button>
            ))}
          </div>

          {/* Phase detail */}
          <div style={{ background: phase.lightColor, border: `1px solid ${phase.color}25`, borderRadius: '20px', padding: 'clamp(28px, 5vw, 48px)', transition: 'all 0.3s ease' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '40px' }}>

              {/* Left: overview */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <span style={{ fontSize: '2rem' }}>{phase.icon}</span>
                  <div>
                    <span style={{ background: phase.color, color: BRAND.white, fontSize: '0.7rem', fontWeight: '800', padding: '3px 12px', borderRadius: '100px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{phase.days}</span>
                  </div>
                </div>
                <h3 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: '800', color: BRAND.dark, lineHeight: '1.1', letterSpacing: '-0.02em', marginBottom: '8px' }}>
                  Phase {phase.number}: {phase.name}
                </h3>
                <p style={{ fontSize: '1rem', fontWeight: '600', color: phase.color, fontStyle: 'italic', marginBottom: '16px' }}>"{phase.tagline}"</p>
                <p style={{ fontSize: '0.9rem', color: '#4A4A5A', lineHeight: '1.7', marginBottom: '16px' }}>{phase.description}</p>
                <div style={{ background: BRAND.white, borderRadius: '10px', padding: '14px 16px', borderLeft: `3px solid ${phase.color}` }}>
                  <p style={{ fontSize: '0.82rem', color: BRAND.muted, lineHeight: '1.55', margin: 0, fontStyle: 'italic' }}>
                    <strong style={{ color: phase.color, fontStyle: 'normal' }}>What to expect: </strong>{phase.whatToExpect}
                  </p>
                </div>
              </div>

              {/* Right: pillar focus + milestones */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div>
                  <p style={{ fontSize: '0.75rem', fontWeight: '700', color: phase.color, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>Daily practice focus</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                    {[{ label: '🧠 Mind', items: phase.mindFocus }, { label: '⚡ Move', items: phase.moveFocus }, { label: '✍️ Journal', items: phase.journalFocus }].map(pillar => (
                      <div key={pillar.label} style={{ background: BRAND.white, borderRadius: '10px', padding: '14px 16px' }}>
                        <p style={{ fontSize: '0.8rem', fontWeight: '700', color: BRAND.dark, marginBottom: '8px' }}>{pillar.label}</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {pillar.items.map(item => (
                            <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '0.78rem', color: BRAND.muted }}>
                              <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: phase.color, flexShrink: 0, marginTop: '6px' }} />
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: '0.75rem', fontWeight: '700', color: phase.color, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>Phase milestones</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {phase.milestones.map(m => (
                      <div key={m} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.82rem', color: '#3A3A4A' }}>
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: phase.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ color: BRAND.white, fontSize: '0.6rem' }}>★</span>
                        </div>
                        {m}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SAMPLE WEEK MODAL ── */}
      {showSampleWeek && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
          onClick={() => setShowSampleWeek(false)}>
          <div style={{ background: BRAND.white, borderRadius: '20px', maxWidth: '860px', width: '100%', maxHeight: '85vh', overflow: 'auto', padding: 'clamp(24px, 4vw, 48px)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <div>
                <div style={{ display: 'inline-flex', background: BRAND.blue, color: BRAND.white, fontSize: '0.7rem', fontWeight: '700', padding: '3px 12px', borderRadius: '100px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Phase 1 · Days 1–7</div>
                <h3 style={{ fontSize: '1.4rem', fontWeight: '800', color: BRAND.dark, margin: 0 }}>Your First Week</h3>
              </div>
              <button onClick={() => setShowSampleWeek(false)} style={{ background: BRAND.light, border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', fontSize: '1.1rem', color: BRAND.muted }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sampleWeek.days.map(day => (
                <div key={day.day} style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '16px', background: BRAND.light, borderRadius: '12px', padding: '16px', alignItems: 'start' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: '700', color: BRAND.blue, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Day</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '900', color: BRAND.dark, lineHeight: 1 }}>{day.day}</div>
                    <div style={{ fontSize: '0.75rem', color: BRAND.muted, fontWeight: '600' }}>{day.theme}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                    {[{ icon: '🧠', label: 'Mind', content: day.mind, color: BRAND.forest }, { icon: '⚡', label: 'Move', content: day.move, color: BRAND.blue }, { icon: '✍️', label: 'Journal', content: day.journal, color: BRAND.sand }].map(p => (
                      <div key={p.label} style={{ background: BRAND.white, borderRadius: '8px', padding: '10px 12px', borderLeft: `3px solid ${p.color}` }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: '700', color: p.color, marginBottom: '4px' }}>{p.icon} {p.label}</div>
                        <div style={{ fontSize: '0.8rem', color: '#4A4A5A', lineHeight: '1.5' }}>{p.content}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: '28px' }}>
              <button onClick={() => navigate('/register')}
                style={{ background: BRAND.forest, color: BRAND.white, fontWeight: '700', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: 'none', cursor: 'pointer', transition: 'all 0.2s ease' }}
                onMouseEnter={e => { e.currentTarget.style.background = BRAND.forestDark; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = BRAND.forest; e.currentTarget.style.transform = 'translateY(0)'; }}>
                Start Day 1 Today
              </button>
              <p style={{ fontSize: '0.8rem', color: BRAND.muted, marginTop: '10px' }}>This is a preview. Your actual Day 1 is personalized to your profile and chosen pathway.</p>
            </div>
          </div>
        </div>
      )}

      {/* ── PERSONAL WELLNESS PLAYBOOK ── */}
      <section style={{ background: BRAND.light, padding: 'clamp(64px, 10vw, 100px) 24px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '48px', alignItems: 'center' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(45,90,71,0.07)', border: '1px solid rgba(45,90,71,0.15)', borderRadius: '100px', padding: '6px 16px', marginBottom: '20px' }}>
                <span style={{ color: BRAND.forest, fontSize: '0.8rem', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Phase 4 Deliverable</span>
              </div>
              <h2 style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)', fontWeight: '800', color: BRAND.dark, lineHeight: '1.15', letterSpacing: '-0.02em', marginBottom: '16px' }}>
                Your Personal Wellness Playbook.
                <br /><span style={{ color: BRAND.forest }}>Built by you. Kept forever.</span>
              </h2>
              <p style={{ fontSize: '0.95rem', color: BRAND.muted, lineHeight: '1.7', marginBottom: '24px' }}>
                In Phase 4, you build a personalized mental health maintenance guide based on 270+ days
                of documented experience. Not a generic wellness plan — your actual data, your proven
                practices, your wellness maintenance strategy, written in your own words.
              </p>
              <p style={{ fontSize: '0.9rem', color: BRAND.muted, lineHeight: '1.65' }}>
                Day 365 isn't the end of the program. It's the moment you stop needing the program.
                The Playbook is what you take with you.
              </p>
            </div>
            <div style={{ background: BRAND.white, borderRadius: '20px', padding: '32px', border: '1px solid #E8E5E1' }}>
              <p style={{ fontSize: '0.75rem', fontWeight: '700', color: BRAND.forest, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '16px' }}>What the Playbook contains</p>
              {[
                { label: 'Your Emotional Profile', desc: 'Documented patterns, stressors, and regulation strategies that actually work for you' },
                { label: 'Your Movement Practice', desc: 'The physical routines that most effectively regulate your nervous system' },
                { label: 'Your Maintenance Journal Format', desc: 'The reflection structure that keeps you honest and self-aware going forward' },
                { label: 'Your Warning Signs & Early Actions', desc: 'What decline looks like for you, and exactly what to do when you see it' },
                { label: 'Your Support Map', desc: 'People, practices, and resources available when things get hard again' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: BRAND.forest, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: BRAND.white, fontSize: '0.65rem', fontWeight: '700' }}>✓</span>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.85rem', fontWeight: '700', color: BRAND.dark, marginBottom: '2px' }}>{item.label}</p>
                    <p style={{ fontSize: '0.78rem', color: BRAND.muted, lineHeight: '1.5', margin: 0 }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="hero-marble-bg" style={{ padding: 'clamp(64px, 10vw, 100px) 24px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: '800', color: '#f1fff8', lineHeight: '1.15', letterSpacing: '-0.02em', marginBottom: '16px' }}>
            365 days from now,
            <br /><span style={{ color: '#ffd166' }}>what do you want to be true?</span>
          </h2>
          <p style={{ fontSize: '1rem', color: 'rgba(241,255,248,0.75)', lineHeight: '1.65', marginBottom: '36px' }}>
            The only way to find out is to start Day 1.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/register')}
              style={{ background: '#ffd166', color: '#041b12', fontWeight: '700', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: 'none', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f2c14e'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffd166'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Start Day 1
            </button>
            <button onClick={() => navigate('/how-it-works')}
              style={{ background: 'transparent', color: BRAND.white, fontWeight: '600', fontSize: '1rem', padding: '14px 32px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.65)'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.background = 'transparent'; }}>
              See How It Works
            </button>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default JourneyPage;
